import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { AuthContext } from "components/authProvider/authProvider";
import { host } from "config";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { generateIncreaseLetterPDF } from "./GenerateLetter";
import { GroupRenewal } from "./types";
import styles from "./renewalLetter.module.css";
const RenewalLetter: React.FC = () => {
  const [letterPDFUrl, setLetterPDFUrl] = useState(null);
  const [groupRenewal, setGroupRenewal] = useState<GroupRenewal>(null);
  const [openRequestChangesDialog, setOpenRequestChangesDialog] = useState(false);
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [successDialogText, setSuccessDialogText] = useState("");

  const { opportunityId } = useParams();

  const authContext = useContext(AuthContext); // Get Auth context
  if (!authContext) {
    throw new Error("AuthContext is undefined");
  }
  const { currentUser } = authContext; // Get current user from Auth context

  useEffect(() => {
    currentUser.getIdToken(false).then((tk) => {
      fetch(`${host}/api/implementation/renewalLetters/${opportunityId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${tk}`,
        },
      }).then((response) => {
        if (response.ok) {
          response.json().then((json) => {
            setGroupRenewal(json.data);
            generateIncreaseLetterPDF(json.data).then((doc) => {
              const blobUrl = doc.output("bloburl");
              setLetterPDFUrl(blobUrl);
            });
          });
        } else {
          response.json().then((json) => alert(json.message));
        }
      });
    });
  }, []);

  const handleCloseRequestChangesDialog = () => {
    setOpenRequestChangesDialog(false);
  };

  const handleCloseSuccessDialog = () => {
    setOpenSuccessDialog(false);
  };

  const handleRequestChangesSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const formJson = Object.fromEntries(formData.entries());
    currentUser.getIdToken(false).then((tk) => {
      fetch(`${host}/api/implementation/renewalLetters/${opportunityId}/requestChanges`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${tk}`,
        },
        body: JSON.stringify(formJson),
      }).then((response) => {
        if (response.ok) {
          handleCloseRequestChangesDialog();
          setSuccessDialogText(
            "Your changes have been successfully requested. An Account Manager will be in contact with you shortly."
          );
          setOpenSuccessDialog(true);
        } else {
          response.json().then((json) => alert(json.message));
        }
      });
    });
  };

  const handleApproveRenewalSubmit = () => {
    currentUser.getIdToken(false).then((tk) => {
      fetch(`${host}/api/implementation/renewalLetters/${opportunityId}/approveRenewal`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${tk}`,
        },
      }).then((response) => {
        if (response.ok) {
          setSuccessDialogText("Your approval of this renewal has been submitted and will be processed shortly.");
          setOpenSuccessDialog(true);
        } else {
          response.json().then((json) => alert(json.message));
        }
      });
    });
  };

  if (!letterPDFUrl) {
    return (
      <div className="loader-container">
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <Dialog open={openRequestChangesDialog} onClose={handleCloseRequestChangesDialog}>
        <DialogTitle>Request Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please explain any changes you would like to make to your proposed renewal and our Account Managers will
            reach out to you.
          </DialogContentText>
          <form
            onSubmit={handleRequestChangesSubmit}
            id="request-changes-form"
            className={styles["request-changes-dialog-form"]}
          >
            <TextField
              autoFocus
              required
              id="requestedChanges"
              name="requestedChanges"
              label="Requested Changes"
              multiline
              fullWidth
              variant="outlined"
              minRows={5}
            />
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRequestChangesDialog}>Cancel</Button>
          <Button type="submit" form="request-changes-form">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openSuccessDialog} onClose={handleCloseSuccessDialog}>
        <DialogTitle>Success</DialogTitle>
        <DialogContent>
          <Typography>{successDialogText}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSuccessDialog} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <Box sx={{ display: "flex", justifyContent: "center", py: 4, height: "fit-content", overflow: "visible" }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            backgroundColor: "#ffffff",
            borderRadius: 4,
            border: "2px solid #000",
            boxShadow: "none",
            overflow: "visible",
            width: "min(1000px, 90%)",
            height: "fit-content",
          }}
        >
          <Typography variant="h4" sx={{ mb: 2 }} textAlign="center">
            {groupRenewal.name} - {groupRenewal.renewalDate.split("T")[0]}
          </Typography>
          <iframe src={letterPDFUrl} title="PDF preview" style={{ width: "100%", height: "60vh", border: 0 }} />
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <Button
              variant="contained"
              size="large"
              style={{ margin: "auto", backgroundColor: "#004e9d" }}
              onClick={() => setOpenRequestChangesDialog(true)}
            >
              Request Changes
            </Button>
            <Button
              variant="contained"
              size="large"
              style={{ margin: "auto", backgroundColor: "#FF8C00" }}
              onClick={handleApproveRenewalSubmit}
            >
              Approve Renewal
            </Button>
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default RenewalLetter;
